import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Database, Upload, Trash2, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, FileJson, FileSpreadsheet, Filter, Plus, X, CheckSquare, Square, EyeOff, Eye } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

import { API_BASE as API } from '../../config';

const OPERATORS = [
  { value: 'equals', label: 'Equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'greater_than', label: 'Greater than' },
  { value: 'less_than', label: 'Less than' },
  { value: 'exists', label: 'Exists' },
  { value: 'not_exists', label: 'Not exists' },
];

const NO_VALUE_OPS = ['exists', 'not_exists'];

const TENANT_SCOPED = ['claims', 'companies', 'claim_notes', 'organizations', 'invites', 'notifications'];

export default function DevDatabase() {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [docTotal, setDocTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [expandedDoc, setExpandedDoc] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadTarget, setUploadTarget] = useState('');
  const [uploading, setUploading] = useState(false);
  const [mappingStep, setMappingStep] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [fieldMapping, setFieldMapping] = useState({});
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTenantId, setUploadTenantId] = useState('');
  const [tenantsList, setTenantsList] = useState([]);
  const fileRef = useRef(null);
  const limit = 20;

  // Filter state
  const [filters, setFilters] = useState([]);
  const [activeFilters, setActiveFilters] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [availableFields, setAvailableFields] = useState([]);

  // Selection state
  const [selectedDocs, setSelectedDocs] = useState(new Set());

  // Demo data filter
  const [hideDemo, setHideDemo] = useState(true);

  // Compress images state
  const [compressing, setCompressing] = useState(false);

  useEffect(() => { fetchCollections(); }, []);

  const fetchCollections = async () => {
    try {
      const res = await axios.get(`${API}/dev/collections`, { withCredentials: true });
      setCollections(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchFields = async (name) => {
    try {
      const res = await axios.get(`${API}/dev/collections/${name}/fields`, { withCredentials: true });
      setAvailableFields(res.data);
    } catch { setAvailableFields([]); }
  };

  const fetchTenants = async () => {
    try {
      const res = await axios.get(`${API}/dev/tenants?include_demo=true`, { withCredentials: true });
      setTenantsList(res.data || []);
    } catch { setTenantsList([]); }
  };

  const fetchDocuments = async (name, pg = 1, filterOverride = null, hideDemoOverride = null) => {
    setSelected(name);
    setPage(pg);
    setExpandedDoc(null);
    setSelectedDocs(new Set());
    const currentFilters = filterOverride !== null ? filterOverride : activeFilters;
    const shouldExcludeDemo = hideDemoOverride !== null ? hideDemoOverride : hideDemo;
    try {
      const params = { page: pg, limit };
      if (currentFilters.length > 0) {
        params.filter = JSON.stringify(currentFilters);
      }
      if (shouldExcludeDemo) {
        params.exclude_demo = true;
      }
      const res = await axios.get(`${API}/dev/collections/${name}`, { params, withCredentials: true });
      setDocuments(res.data.documents);
      setDocTotal(res.data.total);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load documents');
    }
  };

  const handleSelectCollection = (name) => {
    setActiveFilters([]);
    setFilters([]);
    setShowFilters(false);
    setSelectedDocs(new Set());
    fetchFields(name);
    fetchDocuments(name, 1, []);
  };

  const addFilter = () => {
    setFilters(prev => [...prev, { field: availableFields[0] || '', operator: 'equals', value: '' }]);
  };

  const updateFilter = (idx, key, val) => {
    setFilters(prev => prev.map((f, i) => i === idx ? { ...f, [key]: val } : f));
  };

  const removeFilter = (idx) => {
    setFilters(prev => prev.filter((_, i) => i !== idx));
  };

  const applyFilters = () => {
    const valid = filters.filter(f => f.field && f.operator && (NO_VALUE_OPS.includes(f.operator) || f.value));
    setActiveFilters(valid);
    fetchDocuments(selected, 1, valid);
  };

  const clearFilters = () => {
    setFilters([]);
    setActiveFilters([]);
    fetchDocuments(selected, 1, []);
  };

  const toggleHideDemo = () => {
    const newVal = !hideDemo;
    setHideDemo(newVal);
    if (selected) {
      fetchDocuments(selected, 1, activeFilters, newVal);
    }
  };

  // Selection helpers
  const toggleDoc = (docId) => {
    setSelectedDocs(prev => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId); else next.add(docId);
      return next;
    });
  };

  const toggleAll = () => {
    const ids = documents.map(d => d._id).filter(Boolean);
    if (ids.every(id => selectedDocs.has(id))) {
      setSelectedDocs(new Set());
    } else {
      setSelectedDocs(new Set(ids));
    }
  };

  const allSelected = documents.length > 0 && documents.every(d => !d._id || selectedDocs.has(d._id));

  const handleBulkDelete = async () => {
    const ids = [...selectedDocs];
    if (!ids.length) return;
    if (!window.confirm(`Permanently delete ${ids.length} document${ids.length !== 1 ? 's' : ''}?`)) return;
    try {
      const res = await axios.post(`${API}/dev/collections/${selected}/bulk-delete`, { doc_ids: ids }, { withCredentials: true });
      toast.success(res.data.message);
      setSelectedDocs(new Set());
      fetchDocuments(selected, page);
      fetchCollections();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk delete failed');
    }
  };

  const handleCompressImages = async () => {
    setCompressing(true);
    try {
      const res = await axios.post(`${API}/dev/compress-gridfs-images`, {}, { withCredentials: true });
      const d = res.data;
      toast.success(`Compressed ${d.compressed} images, saved ${d.mb_saved} MB`);
      if (d.errors > 0) toast.warning(`${d.errors} file(s) failed`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Compression failed');
    } finally {
      setCompressing(false);
    }
  };

  // Smart auto-mapping: normalize CSV column name to snake_case and fuzzy match against existing fields
  const fuzzyAutoMap = (csvColumns, existingFields) => {
    const normalize = (str) => str.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
    const fieldSet = new Set(existingFields);
    const normalizedLookup = {};
    existingFields.forEach(f => { normalizedLookup[normalize(f)] = f; });

    const autoMap = {};
    csvColumns.forEach(col => {
      // 1. Exact match
      if (fieldSet.has(col)) { autoMap[col] = col; return; }
      // 2. Normalized match (Title Case → snake_case)
      const norm = normalize(col);
      if (normalizedLookup[norm]) { autoMap[col] = normalizedLookup[norm]; return; }
      // 3. Try removing common suffixes/prefixes and partial matches
      // e.g. "PD InAmount" → "pd_inamount", "AL FinAmount" → "al_finamount"
      const variants = [
        norm,
        norm.replace(/_+/g, ''),                    // remove all underscores
        norm.replace(/amount/g, '_amount'),          // "inamount" → "in_amount"  
        norm.replace(/_text$/,'_text'),              // keep as is
      ];
      for (const v of variants) {
        if (normalizedLookup[v]) { autoMap[col] = normalizedLookup[v]; return; }
      }
      // 4. Try closest match: find existing field that starts/ends the same
      const closeMatch = existingFields.find(f => {
        const fn = normalize(f);
        return fn === norm || fn.replace(/_/g, '') === norm.replace(/_/g, '');
      });
      if (closeMatch) { autoMap[col] = closeMatch; return; }
      autoMap[col] = '';
    });
    return autoMap;
  };

  // Upload handlers
  const handlePreview = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file || !uploadTarget) return;
    setUploadFile(file);
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/dev/collections/${uploadTarget}/upload-preview`, formData, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreviewData(res.data);
      const autoMap = fuzzyAutoMap(res.data.columns || [], res.data.existing_fields || []);
      setFieldMapping(autoMap);
      setMappingStep(true);
    } catch (err) { toast.error(err.response?.data?.detail || 'Preview failed'); }
    finally { setUploading(false); }
  };

  const handleMappedUpload = async () => {
    if (!uploadFile || !uploadTarget) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', uploadFile);
    const activeMapping = {};
    Object.entries(fieldMapping).forEach(([src, target]) => { if (target) activeMapping[src] = target; });
    formData.append('field_mapping', JSON.stringify(activeMapping));
    // Inject tenant_id for tenant-scoped collections
    if (uploadTenantId && TENANT_SCOPED.includes(uploadTarget)) {
      formData.append('inject_fields', JSON.stringify({ tenant_id: uploadTenantId }));
    }
    try {
      const res = await axios.post(`${API}/dev/collections/${uploadTarget}/upload`, formData, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(res.data.message);
      resetUpload();
      fetchCollections();
      if (selected === uploadTarget) fetchDocuments(uploadTarget, 1);
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const handleDirectUpload = async () => {
    if (!uploadFile || !uploadTarget) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', uploadFile);
    if (uploadTenantId && TENANT_SCOPED.includes(uploadTarget)) {
      formData.append('inject_fields', JSON.stringify({ tenant_id: uploadTenantId }));
    }
    try {
      const res = await axios.post(`${API}/dev/collections/${uploadTarget}/upload`, formData, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(res.data.message);
      resetUpload();
      fetchCollections();
      if (selected === uploadTarget) fetchDocuments(uploadTarget, 1);
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const resetUpload = () => {
    setShowUpload(false); setMappingStep(false); setPreviewData(null);
    setFieldMapping({}); setUploadFile(null); setUploadTarget(''); setUploadTenantId('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`${API}/dev/collections/${selected}/documents/${docId}`, { withCredentials: true });
      toast.success('Document deleted');
      fetchDocuments(selected, page);
      fetchCollections();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const handleDropCollection = async (name) => {
    if (!window.confirm(`Drop entire collection "${name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/dev/collections/${name}`, { withCredentials: true });
      toast.success(`Collection "${name}" dropped`);
      if (selected === name) { setSelected(null); setDocuments([]); }
      fetchCollections();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to drop collection'); }
  };

  const totalPages = Math.ceil(docTotal / limit);

  const getDocPreview = (doc) => {
    const keys = Object.keys(doc).filter((k) => k !== '_id');
    const preview = {};
    keys.slice(0, 4).forEach((k) => {
      const val = doc[k];
      preview[k] = typeof val === 'string' && val.length > 50 ? val.substring(0, 50) + '...' : val;
    });
    return preview;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-400" /></div>;
  }

  return (
    <div className="p-4 md:p-8" data-testid="dev-database">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Database Explorer</h1>
          <p className="text-sm text-slate-400 mt-1">{collections.length} collection{collections.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleCompressImages} disabled={compressing} variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 h-9 text-sm" data-testid="compress-images-btn">
            {compressing ? 'Compressing...' : 'Compress Images'}
          </Button>
          <Button onClick={() => setShowUpload(!showUpload)} className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 text-sm" data-testid="upload-btn">
            <Upload className="w-4 h-4 mr-1.5" />
            Upload Data
          </Button>
        </div>
      </div>

      {/* Upload Panel */}
      {showUpload && (
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-5 mb-6" data-testid="upload-panel">
          {!mappingStep ? (
            <>
              <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <Upload className="w-4 h-4 text-emerald-400" />
                Upload JSON or CSV
              </h3>
              <form onSubmit={handlePreview} className="flex flex-wrap gap-3 items-end">
                <div className="space-y-1">
                  <label className="text-xs text-slate-400">Collection Name</label>
                  <Input value={uploadTarget} onChange={(e) => setUploadTarget(e.target.value)} placeholder="e.g. claims, companies" className="w-52 bg-slate-800 border-slate-700 text-slate-200 h-9" required data-testid="upload-collection-input" />
                </div>
                {TENANT_SCOPED.includes(uploadTarget) && (
                  <div className="space-y-1">
                    <label className="text-xs text-slate-400">Target Tenant</label>
                    <select
                      value={uploadTenantId}
                      onChange={(e) => setUploadTenantId(e.target.value)}
                      onFocus={() => { if (tenantsList.length === 0) fetchTenants(); }}
                      className="h-9 bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded px-2 outline-none focus:border-emerald-500 min-w-[200px]"
                      data-testid="upload-tenant-select"
                    >
                      <option value="">— no tenant (orphan) —</option>
                      {tenantsList.map(t => (
                        <option key={t.tenant_id} value={t.tenant_id}>{t.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="space-y-1">
                  <label className="text-xs text-slate-400">File (.json or .csv)</label>
                  <input ref={fileRef} type="file" accept=".json,.csv" required className="text-sm text-slate-400 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:font-medium file:bg-slate-700 file:text-slate-300 hover:file:bg-slate-600" data-testid="upload-file-input" />
                </div>
                <Button type="submit" disabled={uploading} className="bg-emerald-600 hover:bg-emerald-700 h-9 text-sm" data-testid="upload-submit-btn">
                  {uploading ? 'Loading...' : 'Preview & Map'}
                </Button>
                <Button type="button" variant="ghost" onClick={resetUpload} className="text-slate-400 h-9 text-sm">Cancel</Button>
              </form>
              <div className="mt-3 flex items-start gap-2 text-xs text-slate-500">
                <FileJson className="w-3.5 h-3.5 mt-0.5 text-amber-500" />
                <span>JSON: Array of objects <code className="text-slate-400">[{"{...}"}, {"{...}"}]</code></span>
                <FileSpreadsheet className="w-3.5 h-3.5 mt-0.5 ml-3 text-green-500" />
                <span>CSV: Header row + data rows</span>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  <Upload className="w-4 h-4 text-emerald-400" />
                  Field Mapping — {previewData?.row_count} rows into <span className="text-emerald-400">{uploadTarget}</span>
                  {uploadTenantId && tenantsList.length > 0 && (
                    <span className="text-xs text-blue-400 ml-2">→ {tenantsList.find(t => t.tenant_id === uploadTenantId)?.name || uploadTenantId}</span>
                  )}
                </h3>
                <div className="flex gap-2">
                  <Button variant="ghost" onClick={() => setMappingStep(false)} className="text-slate-400 h-8 text-xs">Back</Button>
                  <Button variant="ghost" onClick={resetUpload} className="text-slate-400 h-8 text-xs">Cancel</Button>
                </div>
              </div>
              {/* Mapping summary bar */}
              {(() => {
                const mapped = Object.values(fieldMapping).filter(v => v).length;
                const total = Object.keys(fieldMapping).length;
                const pct = total > 0 ? Math.round((mapped / total) * 100) : 0;
                return (
                  <div className="flex items-center gap-3 mb-3 px-1">
                    <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-xs text-slate-400 whitespace-nowrap">{mapped}/{total} mapped ({pct}%)</span>
                    <button onClick={() => {
                      const cleared = {};
                      Object.keys(fieldMapping).forEach(k => { cleared[k] = ''; });
                      setFieldMapping(cleared);
                    }} className="text-[10px] text-slate-500 hover:text-red-400 transition-colors whitespace-nowrap" data-testid="clear-all-mapping">Clear All</button>
                    <button onClick={() => {
                      const reMap = fuzzyAutoMap(previewData?.columns || [], previewData?.existing_fields || []);
                      setFieldMapping(reMap);
                    }} className="text-[10px] text-emerald-500 hover:text-emerald-400 transition-colors whitespace-nowrap" data-testid="auto-map-btn">Auto-Map</button>
                  </div>
                );
              })()}
              <div className="bg-slate-800/50 rounded-lg overflow-hidden mb-4">
                <div className="grid grid-cols-[1fr_auto_1fr] gap-0 px-4 py-2 border-b border-slate-700">
                  <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Source Column</span>
                  <span className="w-8" />
                  <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Target Field</span>
                </div>
                <div className="divide-y divide-slate-800/50 max-h-48 overflow-y-auto">
                  {(previewData?.columns || []).map(col => (
                    <div key={col} className={`grid grid-cols-[1fr_auto_1fr] gap-0 items-center px-4 py-2 ${fieldMapping[col] ? 'bg-emerald-500/5' : ''}`} data-testid={`mapping-row-${col}`}>
                      <span className="text-sm text-slate-300 font-mono flex items-center gap-2">
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${fieldMapping[col] ? 'bg-emerald-500' : 'bg-slate-600'}`} />
                        {col}
                      </span>
                      <span className="text-slate-600 px-3">&#8594;</span>
                      <div className="flex gap-2">
                        <select value={fieldMapping[col] || ''} onChange={(e) => setFieldMapping(prev => ({ ...prev, [col]: e.target.value }))} className="flex-1 bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded px-2 py-1.5 outline-none focus:border-emerald-500" data-testid={`mapping-select-${col}`}>
                          <option value="">— skip —</option>
                          {(previewData?.existing_fields || []).map(f => (<option key={f} value={f}>{f}</option>))}
                          <option value={col}>{col} (keep as-is)</option>
                        </select>
                        <input type="text" placeholder="custom name" className="w-32 bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded px-2 py-1.5 outline-none focus:border-emerald-500" onChange={(e) => { if (e.target.value) setFieldMapping(prev => ({ ...prev, [col]: e.target.value })); }} data-testid={`mapping-custom-${col}`} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {previewData?.preview_rows?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-2">Data Preview (first {previewData.preview_rows.length} rows)</h4>
                  <div className="overflow-x-auto rounded-lg border border-slate-800">
                    <table className="w-full text-xs">
                      <thead><tr className="bg-slate-800/60">
                        {(previewData.columns || []).map(col => (
                          <th key={col} className="px-3 py-2 text-left text-slate-400 font-medium whitespace-nowrap">
                            {fieldMapping[col] ? <span className="text-emerald-400">{fieldMapping[col]}</span> : <span className="text-slate-600 line-through">{col}</span>}
                          </th>
                        ))}
                      </tr></thead>
                      <tbody>
                        {previewData.preview_rows.map((row, i) => (
                          <tr key={row._id || row.id || i} className="border-t border-slate-800/50">
                            {(previewData.columns || []).map(col => (
                              <td key={col} className={`px-3 py-1.5 whitespace-nowrap ${fieldMapping[col] ? 'text-slate-300' : 'text-slate-600'}`}>
                                {String(row[col] ?? '').substring(0, 40)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              <div className="flex gap-3">
                <Button onClick={handleMappedUpload} disabled={uploading || !Object.values(fieldMapping).some(v => v)} className="bg-emerald-600 hover:bg-emerald-700 h-9 text-sm" data-testid="confirm-mapped-upload">
                  {uploading ? 'Importing...' : `Import with Mapping (${Object.values(fieldMapping).filter(v => v).length} fields)`}
                </Button>
                <Button onClick={handleDirectUpload} disabled={uploading} variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 h-9 text-sm" data-testid="direct-upload-btn">
                  Import As-Is (no mapping)
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      <div className="flex gap-6">
        {/* Collections List */}
        <div className="w-64 flex-shrink-0">
          <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-800">
              <h3 className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Collections</h3>
            </div>
            <div className="divide-y divide-slate-800">
              {collections.map((c) => (
                <div
                  key={c.name}
                  className={`group flex items-center justify-between px-4 py-2.5 cursor-pointer transition-colors ${selected === c.name ? 'bg-slate-800/80 text-emerald-400' : 'text-slate-300 hover:bg-slate-800/40'}`}
                  onClick={() => handleSelectCollection(c.name)}
                  data-testid={`collection-${c.name}`}
                >
                  <div className="flex items-center gap-2">
                    <Database className="w-3.5 h-3.5 text-slate-500" />
                    <span className="text-sm">{c.name}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] text-slate-500">{c.document_count}</span>
                    <button onClick={(e) => { e.stopPropagation(); handleDropCollection(c.name); }} className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-400 transition-opacity p-0.5" title="Drop collection">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
              {collections.length === 0 && (
                <p className="px-4 py-4 text-xs text-slate-500 text-center">No collections</p>
              )}
            </div>
          </div>
        </div>

        {/* Documents View */}
        <div className="flex-1">
          {selected ? (
            <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
              {/* Header */}
              <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-white">{selected}</h3>
                    <p className="text-[10px] text-slate-500">{docTotal} document{docTotal !== 1 ? 's' : ''}{activeFilters.length > 0 ? ' (filtered)' : ''}{hideDemo ? ' (demo hidden)' : ''}</p>
                  </div>
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-xs transition-colors ${showFilters || activeFilters.length > 0 ? 'bg-emerald-600/20 text-emerald-400' : 'text-slate-400 hover:bg-slate-800'}`}
                    data-testid="toggle-filters-btn"
                  >
                    <Filter className="w-3.5 h-3.5" />
                    Filters
                    {activeFilters.length > 0 && <span className="bg-emerald-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">{activeFilters.length}</span>}
                  </button>
                  <button
                    onClick={toggleHideDemo}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-xs transition-colors ${hideDemo ? 'bg-amber-500/20 text-amber-400' : 'text-slate-400 hover:bg-slate-800'}`}
                    data-testid="toggle-hide-demo-btn"
                    title={hideDemo ? 'Demo data is hidden — click to show' : 'Showing all data — click to hide demo'}
                  >
                    {hideDemo ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    {hideDemo ? 'Demo Hidden' : 'All Data'}
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  {/* Bulk action bar */}
                  {selectedDocs.size > 0 && (
                    <Button onClick={handleBulkDelete} className="bg-red-600 hover:bg-red-700 text-white h-7 text-xs px-3" data-testid="bulk-delete-btn">
                      <Trash2 className="w-3 h-3 mr-1.5" />
                      Delete Selected ({selectedDocs.size})
                    </Button>
                  )}
                  {totalPages > 1 && (
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => fetchDocuments(selected, page - 1)} className="h-7 w-7 p-0 text-slate-400">
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="text-xs text-slate-500">{page} / {totalPages}</span>
                      <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => fetchDocuments(selected, page + 1)} className="h-7 w-7 p-0 text-slate-400">
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Filter Panel */}
              {showFilters && (
                <div className="px-5 py-3 border-b border-slate-800 bg-slate-800/30" data-testid="filter-panel">
                  <div className="space-y-2">
                    {filters.map((f, idx) => (
                      <div key={idx} className="flex items-center gap-2 flex-wrap" data-testid={`filter-row-${idx}`}>
                        <select value={f.field} onChange={(e) => updateFilter(idx, 'field', e.target.value)} className="bg-slate-800 border border-slate-700 text-slate-200 text-xs rounded px-2 py-1.5 outline-none focus:border-emerald-500 min-w-[120px]" data-testid={`filter-field-${idx}`}>
                          <option value="">Select field</option>
                          {availableFields.map(field => (<option key={field} value={field}>{field}</option>))}
                        </select>
                        <select value={f.operator} onChange={(e) => updateFilter(idx, 'operator', e.target.value)} className="bg-slate-800 border border-slate-700 text-slate-200 text-xs rounded px-2 py-1.5 outline-none focus:border-emerald-500" data-testid={`filter-op-${idx}`}>
                          {OPERATORS.map(op => (<option key={op.value} value={op.value}>{op.label}</option>))}
                        </select>
                        {!NO_VALUE_OPS.includes(f.operator) && (
                          <Input value={f.value} onChange={(e) => updateFilter(idx, 'value', e.target.value)} placeholder="Value..." className="w-40 bg-slate-800 border-slate-700 text-slate-200 h-7 text-xs" data-testid={`filter-value-${idx}`} />
                        )}
                        <button onClick={() => removeFilter(idx)} className="text-slate-500 hover:text-red-400 p-1" data-testid={`filter-remove-${idx}`}>
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <button onClick={addFilter} className="flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300 transition-colors" data-testid="add-filter-btn">
                      <Plus className="w-3.5 h-3.5" />
                      Add Filter
                    </button>
                    {filters.length > 0 && (
                      <>
                        <Button onClick={applyFilters} className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs px-3" data-testid="apply-filters-btn">
                          Apply
                        </Button>
                        <button onClick={clearFilters} className="text-xs text-slate-500 hover:text-slate-300 transition-colors" data-testid="clear-filters-btn">
                          Clear All
                        </button>
                      </>
                    )}
                    {filters.length === 0 && activeFilters.length > 0 && (
                      <button onClick={clearFilters} className="text-xs text-slate-500 hover:text-slate-300 transition-colors" data-testid="clear-filters-btn">
                        Clear Active Filters
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Documents list */}
              <div className="divide-y divide-slate-800">
                {/* Select All row */}
                {documents.length > 0 && (
                  <div className="px-5 py-2 flex items-center gap-3 bg-slate-800/20">
                    <button onClick={toggleAll} className="text-slate-400 hover:text-emerald-400 transition-colors" data-testid="select-all-btn">
                      {allSelected ? <CheckSquare className="w-4 h-4 text-emerald-400" /> : <Square className="w-4 h-4" />}
                    </button>
                    <span className="text-[10px] text-slate-500 uppercase tracking-wider">
                      {selectedDocs.size > 0 ? `${selectedDocs.size} selected` : 'Select all'}
                    </span>
                  </div>
                )}
                {documents.map((doc, idx) => {
                  const isExpanded = expandedDoc === idx;
                  const isChecked = doc._id && selectedDocs.has(doc._id);
                  return (
                    <div key={doc._id || idx} className={`transition-colors ${isChecked ? 'bg-emerald-500/5' : 'hover:bg-slate-800/30'}`}>
                      <div className="px-5 py-3 flex items-center gap-3 cursor-pointer" onClick={() => setExpandedDoc(isExpanded ? null : idx)}>
                        {/* Checkbox */}
                        {doc._id && (
                          <button onClick={(e) => { e.stopPropagation(); toggleDoc(doc._id); }} className="text-slate-400 hover:text-emerald-400 transition-colors shrink-0" data-testid={`select-doc-${doc._id}`}>
                            {isChecked ? <CheckSquare className="w-4 h-4 text-emerald-400" /> : <Square className="w-4 h-4" />}
                          </button>
                        )}
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <span className="text-[10px] text-slate-600 font-mono w-5 text-right flex-shrink-0">
                            {(page - 1) * limit + idx + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap gap-x-4 gap-y-0.5">
                              {Object.entries(getDocPreview(doc)).map(([k, v]) => (
                                <span key={k} className="text-xs">
                                  <span className="text-slate-500">{k}:</span>{' '}
                                  <span className="text-slate-300">{typeof v === 'boolean' ? String(v) : String(v ?? '')}</span>
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                          {doc._id && (
                            <button onClick={(e) => { e.stopPropagation(); handleDeleteDoc(doc._id); }} className="text-red-500/60 hover:text-red-400 active:text-red-300 transition-colors p-2 -m-1 rounded-md hover:bg-red-500/10" data-testid={`delete-doc-${doc._id}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                          {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                        </div>
                      </div>
                      {isExpanded && (
                        <div className="px-5 pb-4">
                          <pre className="text-xs text-slate-300 bg-slate-800/60 rounded p-3 overflow-auto max-h-80 font-mono">
                            {JSON.stringify(doc, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  );
                })}
                {documents.length === 0 && (
                  <p className="px-5 py-8 text-sm text-slate-500 text-center">
                    {activeFilters.length > 0 ? 'No documents match your filters' : 'Collection is empty'}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-slate-600">
              <div className="text-center">
                <Database className="w-8 h-8 mx-auto mb-2" />
                <p className="text-sm">Select a collection to browse</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
